import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ICreatedIntegrationApiKey,
  ICreateIntegrationApiKeyRequest,
  IIntegrationApiKey,
  IUpdateIntegrationApiKeyRequest,
} from "../../../@types/admin/AdminIntegrationApiKey";
import { integrationApiKeysService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { INTEGRATION_API_KEYS_QUERY_KEY } from "../../queries/admin/integrationApiKeys";

/**
 * Mints a new key. The result carries `plainKey` ONCE; the caller shows it
 * and must not persist, log or toast it. The list is invalidated so the new
 * row (prefix only) appears once the modal closes.
 *
 * The mutation result is the plain key too, so it must not outlive the copy
 * panel: `gcTime: 0` drops it from the mutation cache as soon as nothing
 * observes it, and the caller calls `resetCreateApiKey` when the panel closes.
 */
export function useCreateIntegrationApiKey() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, reset } = useMutation<
    ICreatedIntegrationApiKey,
    Error,
    ICreateIntegrationApiKeyRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await integrationApiKeysService.createApiKey(request)),
    gcTime: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INTEGRATION_API_KEYS_QUERY_KEY],
      });
    },
  });

  return {
    createApiKey: mutateAsync,
    isCreatingApiKey: isPending,
    resetCreateApiKey: reset,
  };
}

/** Edit, revoke (`active: false`) and reactivate (`active: true`) all go here. */
export function useUpdateIntegrationApiKey() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IIntegrationApiKey,
    Error,
    { id: string; request: IUpdateIntegrationApiKeyRequest }
  >({
    mutationFn: async ({ id, request }) =>
      ensureOk(await integrationApiKeysService.updateApiKey(id, request)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INTEGRATION_API_KEYS_QUERY_KEY],
      });
    },
  });

  return { updateApiKey: mutateAsync, isUpdatingApiKey: isPending };
}
